import { Row } from 'components/GridItemList';
import Loader from 'components/Loader';
import { SectionEdit, SectionEditForm } from 'components/section-edit/SectionEdit';
import { useFrozenOnceLoaded } from 'components/section-edit/useFrozenOnceLoaded';
import { saveSection } from 'components/section-edit/saveSection';
import { ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { useGetProjectByIdQuery, useUpdateProjectMutation } from 'store/api/projects';
import { ClassifierData } from 'types/classifiers';
import { ProjectDetailsDto, ProjectFormData } from 'types/projects';
import { useClassifiers } from 'utils/useClassifiers';
import {
    PROJECT_SECTION_FIELDS,
    ProjectSectionId,
    projectDtoToForm,
    projectFormToRequest,
} from '../projectFormMapping';
import {
    ProjectBillingFields,
    ProjectCapFields,
    ProjectCasesFields,
    ProjectFormState,
    ProjectOverviewFields,
    ProjectTeamFields,
} from './ProjectFields';

const SECTION_FIELDS: Record<ProjectSectionId, (form: ProjectFormState) => ReactNode> = {
    overview: (form) => <ProjectOverviewFields form={form} />,
    billing: (form) => <ProjectBillingFields form={form} />,
    team: (form) => <ProjectTeamFields form={form} />,
    cap: (form) => <ProjectCapFields form={form} />,
    cases: (form) => <ProjectCasesFields form={form} />,
};

interface SectionFormProps {
    section: ProjectSectionId;
    projectId: number;
    initialData: ProjectFormData;
    classifiers: Record<string, ClassifierData>;
    onClose: () => void;
}

function ProjectSectionForm({ section, projectId, initialData, classifiers, onClose }: SectionFormProps) {
    const methods = useForm<ProjectFormData>({ defaultValues: initialData });
    // The page's own cache entry: it reads the project by the route's id.
    const { id = '' } = useParams();
    const { refetch } = useGetProjectByIdQuery(id);
    const [updateProject] = useUpdateProjectMutation();

    const onSave = (values: ProjectFormData) =>
        saveSection(values, {
            section,
            sectionFields: PROJECT_SECTION_FIELDS,
            fetchLatest: () => refetch().unwrap(),
            toForm: (dto) => projectDtoToForm(dto, classifiers),
            toRequest: (edited) => projectFormToRequest(edited),
            update: (request) => updateProject({ ...request, id: projectId }).unwrap(),
            onSaved: onClose,
        });

    return (
        <SectionEditForm methods={methods} onSave={onSave} onCancel={onClose}>
            {SECTION_FIELDS[section]({ methods, classifiers })}
        </SectionEditForm>
    );
}

function ProjectSectionEditor({
    section,
    project,
    onClose,
}: {
    section: ProjectSectionId;
    project: ProjectDetailsDto;
    onClose: () => void;
}) {
    const { classifiers, isLoading } = useClassifiers({ codes: ['CURRENCY'] });
    const initialData = useFrozenOnceLoaded(!isLoading, () => projectDtoToForm(project, classifiers));
    if (!initialData) {
        return <Loader />;
    }
    return (
        <ProjectSectionForm
            section={section}
            projectId={project.id}
            initialData={initialData}
            classifiers={classifiers}
            onClose={onClose}
        />
    );
}

interface ProjectSectionProps {
    id: ProjectSectionId;
    titleId: string;
    project: ProjectDetailsDto;
    /** The read view, handed the section's Edit control. */
    children: (edit: ReactNode) => ReactNode;
    /** The read view's rows: those no field edits stay under the fields while the section edits. */
    rows?: Row[];
}

/** A project card on the record page that edits in place with the project form's fields for that card. */
export function ProjectSection({ id, titleId, project, children, rows }: ProjectSectionProps) {
    return (
        <SectionEdit
            id={id}
            titleId={titleId}
            editor={(close) => <ProjectSectionEditor section={id} project={project} onClose={close} />}
            rows={rows}
        >
            {children}
        </SectionEdit>
    );
}
